import { Component, Input, Output, EventEmitter, signal, computed, OnInit, OnChanges, SimpleChanges, ElementRef, HostListener, inject, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { TranslationService } from '../../../services/TranslationService';
import { TimeZoneIconComponent } from '../timezone-icon/timezone-icon.component';

@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, OverlayModule, TimeZoneIconComponent],
  templateUrl: './date-time-picker.component.html'
})
export class DateTimePickerComponent implements OnInit, OnChanges {

  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;
  private viewContainerRef = inject(ViewContainerRef);

  @ViewChild('dateTimePickerButton') buttonRef!: ElementRef<HTMLElement>;
  @ViewChild('popupTemplate') popupTemplate!: TemplateRef<any>;

  // Valeur sélectionnée (Date complète)
  private _dateTimeSignal = signal<Date | null>(null);
  private translationService = inject(TranslationService)


  @Input() set selectedDateTime(value: Date | null) {
    let dateValue = value ? new Date(value) : new Date();

    this._dateTimeSignal.set(value ? dateValue : null);
    if (value) {
      this.updateCalendarView(dateValue);
    }
  }
  get selectedDateTime(): Date | null {
    return this._dateTimeSignal();
  }

  @Output() dateTimeChange = new EventEmitter<Date | null>();

  // Paramètres d'affichage
  @Input() timeFormat: string = this.translationService.instant('app.time-output-format')?.toLowerCase().trim() || '24h';
  @Input() disabled: boolean = false;

  // Fuseau du site edite quand il differe du lecteur : le champ, lui, affiche l'heure du LECTEUR.
  @Input() siteTimeZone: string | null = null;
  @Input() calendarAlign: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' = 'bottom-right';



  // Affichage du popup
  show = signal(false);

  // elementRef = Référence pour la détection de clic extérieur
  constructor(private elementRef: ElementRef) {
    let AAA = 1
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.show.set(false);
    }
  }

  // --- Calendrier (mois) ---
  private today = new Date();
  viewYear = signal(0);
  viewMonth = signal(0);

  currentMonthAndYear = computed(() => new Date(this.viewYear(), this.viewMonth()));

  ngOnInit(): void {
    if (this.selectedDateTime) {
      this.updateCalendarView(this.selectedDateTime);
    } else {
      this.updateCalendarView(this.today);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDateTime'] && changes['selectedDateTime'].currentValue) {
      this.updateCalendarView(changes['selectedDateTime'].currentValue);
    }
  }

  private updateCalendarView(raw: Date | string | null | undefined): void {
    const date = this.normalizeDate(raw);

    this.viewYear.set(date.getFullYear());
    this.viewMonth.set(date.getMonth());
  }

  private normalizeDate(value: Date | string | null | undefined): Date {
    if (!value) return new Date();

    if (value instanceof Date) return value;

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;

    return new Date(); // fallback en mode bulldowzer
  }

  daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  get calendar(): (number | null)[] {
    const year = this.viewYear();
    const month = this.viewMonth();
    const days = this.daysInMonth(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    // Ajuste pour que le lundi soit le premier (0 = lundi, ... 6 = dimanche)
    const adjustedFirstDay = (firstDay + 6) % 7;
    const blanks = Array(adjustedFirstDay).fill(null);
    return [...blanks, ...Array.from({ length: days }, (_, i) => i + 1)];
  }

  prevMonth(): void {
    const m = this.viewMonth();
    const y = this.viewYear();
    if (m === 0) {
      this.viewMonth.set(11);
      this.viewYear.set(y - 1);
    } else {
      this.viewMonth.set(m - 1);
    }
  }

  nextMonth(): void {
    const m = this.viewMonth();
    const y = this.viewYear();
    if (m === 11) {
      this.viewMonth.set(0);
      this.viewYear.set(y + 1);
    } else {
      this.viewMonth.set(m + 1);
    }
  }

  // Sélection d'un jour
  selectDay(day: number | null): void {
    if (!day) return;
    let newDate = new Date(this.viewYear(), this.viewMonth(), day);
    // Conserver l'heure actuelle si elle existe, sinon 0h
    const current = this.selectedDateTime;
    if (current) {
      newDate.setHours(current.getHours(), current.getMinutes(), current.getSeconds(), current.getMilliseconds());
    } else {
      newDate.setHours(0, 0, 0, 0);
    }
    this.applyDateTime(newDate);
  }

  // --- Heure / Minutes ---
  hour = signal(0);
  minute = signal(0);
  ampm = signal<'AM' | 'PM'>('AM');

  // Met à jour les champs heure/minutes à partir de la date
  private updateTimeFields(date: Date): void {
    let hours = date.getHours();
    if (this.timeFormat == '12h') {
      const am = hours < 12;
      this.ampm.set(am ? 'AM' : 'PM');
      let h12 = hours % 12;
      if (h12 === 0) h12 = 12;
      this.hour.set(h12);
    } else {
      this.hour.set(hours);
    }
    this.minute.set(date.getMinutes());
  }

  // Applique une nouvelle date (combinaison jour + heure/minute)
  private applyDateTime(newDate: Date): void {
    this._dateTimeSignal.set(newDate);
    this.updateTimeFields(newDate);
    this.dateTimeChange.emit(newDate);
  }

  // Appelé quand on change l'heure ou la minute
  onTimeChange(): void {
    const current = this.selectedDateTime;
    if (!current) return;
    let hours = this.timeFormat == '12h'
      ? (this.hour() % 12) + (this.ampm() == 'PM' ? 12 : 0)
      : this.hour();
    const newDate = new Date(current);
    newDate.setHours(hours, this.minute(), 0, 0);
    this.applyDateTime(newDate);
  }

  setNow(): void {
    const now = new Date();
    this.applyDateTime(now);
    this.updateCalendarView(now);
  }

  clear(): void {
    const defaultTime = new Date();
    defaultTime.setHours(0, 0, 0, 0);

    this._dateTimeSignal.set(defaultTime);
    this.dateTimeChange.emit(null);
    this.show.set(false);

  }

  get formattedDate(): string {
    const dt = this.selectedDateTime;
    if (!dt) return 'select-date-time';
    const datePart = dt.toLocaleDateString();
    return datePart;
  }

  get formattedTime(): string {
    const dt = this.selectedDateTime;
    if (!dt) return 'select-date-time';
    let timePart = '';
    if (this.timeFormat == '12h') {
      let hours = dt.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      timePart = `${hours} h ${dt.getMinutes().toString().padStart(2, '0')} min ${ampm}`;
    } else {
      timePart = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return timePart;
  }

  public formattedMonthYear = computed(() => {
    const date = this.currentMonthAndYear();
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  });



  public get monthName(): string {
    let month = this.currentMonthAndYear().toLocaleString('en-US', { month: 'long' });
    return month;
  }


  openPopup(): void {
    if (this.disabled) return;
    this.syncTimeFieldsFromSelectedDate();
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef = null;
    }

    // Positionner l'overlay par rapport au bouton
    const positionStrategy = this.getPositionStrategy();
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.attach(new TemplatePortal(this.popupTemplate, this.viewContainerRef));
    this.show.set(true);

    // pour un close on ouside click
    this.overlayRef.backdropClick().subscribe(() => this.closePopup());
  }

  closePopup(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef = null;
    }
    this.show.set(false);
  }

  private getPositionStrategy() {
    const buttonElement = this.buttonRef.nativeElement;
    let originX: 'start' | 'center' | 'end';
    let originY: 'top' | 'bottom' | 'center';
    let overlayX: 'start' | 'center' | 'end';
    let overlayY: 'top' | 'bottom' | 'center';
    let offsetX = 0;
    let offsetY = 0;

    switch (this.calendarAlign) {
      case 'top-left':
        originX = 'start'; originY = 'top';
        overlayX = 'start'; overlayY = 'bottom';
        offsetY = -8;
        break;
      case 'top-center':
        originX = 'center'; originY = 'top';
        overlayX = 'center'; overlayY = 'bottom';
        offsetY = -8;
        break;
      case 'top-right':
        originX = 'end'; originY = 'top';
        overlayX = 'end'; overlayY = 'bottom';
        offsetY = -8;
        break;
      case 'bottom-left':
        originX = 'start'; originY = 'bottom';
        overlayX = 'start'; overlayY = 'top';
        offsetY = 8;
        break;
      case 'bottom-center':
        originX = 'center'; originY = 'bottom';
        overlayX = 'center'; overlayY = 'top';
        offsetY = 8;
        break;
      default: // bottom-right
        originX = 'end'; originY = 'bottom';
        overlayX = 'end'; overlayY = 'top';
        offsetY = 8;
        break;
    }

    return this.overlay.position()
      .flexibleConnectedTo(buttonElement)
      .withPositions([{
        originX,
        originY,
        overlayX,
        overlayY,
        offsetY,
      }]);
  }


  onButtonClick(): void {
    if (this.disabled) return;
    this.openPopup();
  }


  ngOnDestroy(): void {
    this.closePopup();
  }


  private syncTimeFieldsFromSelectedDate(): void {
    const dt = this.selectedDateTime;
    if (dt) {
      this.updateTimeFields(dt);
    } else {
      // Aucune date sélectionnée : on affiche l’heure  00:00 
      const defaultTime = new Date();
      defaultTime.setHours(0, 0, 0, 0);
      this.updateTimeFields(defaultTime);
    }
  }

  onHourInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = parseInt(input.value, 10);
    if (isNaN(val)) val = 0;

    if (this.timeFormat == '12h') {
      if (val < 1) val = 1;
      if (val > 12) val = 12;
    } else {
      if (val < 0) val = 0;
      if (val > 23) val = 23;
    }

    this.hour.set(val);
    this.onTimeChange();
  }

  onMinuteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = parseInt(input.value, 10);
    if (isNaN(val)) val = 0;

    if (val < 0) val = 0;
    if (val > 59) val = 59;

    this.minute.set(val);
    this.onTimeChange();
  }


  incrementHour(): void {
    let h = this.hour();
    if (this.timeFormat == '12h') {
      h = (h % 12) + 1;   // cycle 1-12
    } else {
      h = (h + 1) % 24;
    }
    this.hour.set(h);
    this.onTimeChange();
  }

  decrementHour(): void {
    let h = this.hour();
    if (this.timeFormat == '12h') {
      h = ((h - 2 + 12) % 12) + 1; // cycle 1-12 inversé
    } else {
      h = (h - 1 + 24) % 24;
    }
    this.hour.set(h);
    this.onTimeChange();
  }

  incrementMinute(): void {
    let m = (this.minute() + 1) % 60;
    this.minute.set(m);
    this.onTimeChange();
  }

  decrementMinute(): void {
    let m = (this.minute() - 1 + 60) % 60;
    this.minute.set(m);
    this.onTimeChange();
  }

}
